import { RECONNECT_INITIAL_MS, nextBackoffMs, parseLiveEvent, tokenExpiresAt, type LiveEvent } from './liveUpdates';

/**
 * Admin's one /admin/live connection: the stateful half of
 * LiveUpdatesProvider, apart from React, the auth store and the browser
 * globals, which the provider passes in (so the tests can pass fakes).
 *
 * Connection: opened with the token the auth store holds, reopened when
 * that token changes, closed on sign-out. A stream the browser gave up on
 * is reopened with backoff, 1 s to 30 s; when the stored token has expired
 * too, an authenticated read runs the refresh hook first and the new token
 * reconnects through the store.
 *
 * Health: open, and the Api says its live bus is live. The connected ack
 * sets it from `live`. Since Codex review finding 8 (29 Oct 2026) the Api
 * also says when that changes on an open stream: `degraded` (a bus stream
 * was lost, events may be missing) clears it, so the safety net polls
 * again; `recovered` (every bus stream confirmed again) sets it and runs
 * one catch-up, because what changed in the gap was never sent. An error
 * clears it too, and a reconnect's ack runs the same catch-up.
 *
 * Safety net: while not healthy, a catch-up once a minute in a visible tab,
 * so a page never goes stale without a working stream. Healthy, nothing
 * polls.
 */

/** The safety net's cadence, only while the stream is unhealthy. */
export const DEGRADED_REFRESH_MS = 60_000;

/** EventSource.CLOSED: the browser gave up and will not retry on its own. */
const SOURCE_CLOSED = 2;

/** The part of EventSource the stream uses. */
export interface LiveSource {
  readonly readyState: number;
  onerror: ((ev: Event) => void) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
}

export interface LiveStreamDeps {
  /** The stream URL for a token. */
  url: (token: string) => string;
  /** `new EventSource(url)` in the browser. */
  openSource: (url: string) => LiveSource;
  /** The access token the auth store holds now. */
  getToken: () => string | null;
  /** Subscribe to token changes; returns the unsubscribe. */
  onTokenChange: (listener: (token: string | null, previous: string | null) => void) => () => void;
  /** Any authenticated read, which runs the 401 refresh hook. */
  refreshToken: () => void;
  /** The tab is visible; the safety net skips hidden tabs. */
  isVisible: () => boolean;
}

export interface LiveStreamHandlers {
  onEvent: (e: LiveEvent) => void;
  /** Everything live is stale: refetch it once. */
  onCatchUp: () => void;
}

export interface LiveStream {
  stop: () => void;
  /** Open, and the Api says its live bus is live. */
  isHealthy: () => boolean;
}

export function startLiveStream(handlers: LiveStreamHandlers, deps: LiveStreamDeps): LiveStream {
  let source: LiveSource | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let backoff = RECONNECT_INITIAL_MS;
  let healthy = false;
  let connectedBefore = false;
  let stopped = false;

  const close = (): void => {
    if (source) source.close();
    source = null;
    healthy = false;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  };

  const scheduleRetry = (): void => {
    if (retryTimer || stopped) return;
    const delay = backoff;
    backoff = nextBackoffMs(backoff);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      const token = deps.getToken();
      if (!token) return; // signed out meanwhile
      const exp = tokenExpiresAt(token);
      if (exp !== null && exp <= Date.now()) {
        // The stored token is spent too. Any authenticated read runs the
        // 401 refresh hook (lib/api.ts); a new token reconnects through the
        // store subscription, which cancels this retry loop.
        deps.refreshToken();
        scheduleRetry();
        return;
      }
      connect(token);
    }, delay);
  };

  const connect = (token: string): void => {
    close();
    const es = deps.openSource(deps.url(token));
    source = es;
    es.addEventListener('connected', (ev: MessageEvent) => {
      backoff = RECONNECT_INITIAL_MS;
      let live = false;
      try {
        live = (JSON.parse(ev.data as string) as { live?: unknown }).live === true;
      } catch {
        // A malformed ack counts as no bus.
      }
      healthy = live;
      // A reconnect: whatever changed during the gap is stale now.
      if (connectedBefore) handlers.onCatchUp();
      connectedBefore = true;
    });
    es.addEventListener('live', (ev: MessageEvent) => {
      const e = parseLiveEvent(ev.data as string);
      if (e) handlers.onEvent(e);
    });
    // The bus lost a stream: events may be missing, so the safety net runs.
    es.addEventListener('degraded', () => {
      if (source === es) healthy = false;
    });
    // Every bus stream is confirmed again: stop polling, catch up once.
    es.addEventListener('recovered', () => {
      if (source !== es) return;
      healthy = true;
      handlers.onCatchUp();
    });
    es.onerror = () => {
      healthy = false;
      // CONNECTING: the browser is retrying on its own. CLOSED: it gave up.
      if (es.readyState === SOURCE_CLOSED && source === es) scheduleRetry();
    };
  };

  const unsubscribe = deps.onTokenChange((token, previous) => {
    if (token && token !== previous) connect(token);
    else if (!token && previous) close();
  });
  const initial = deps.getToken();
  if (initial) connect(initial);

  const safetyNet = setInterval(() => {
    if (healthy || !deps.getToken()) return;
    if (!deps.isVisible()) return;
    handlers.onCatchUp();
  }, DEGRADED_REFRESH_MS);

  return {
    stop() {
      stopped = true;
      unsubscribe();
      clearInterval(safetyNet);
      close();
    },
    isHealthy: () => healthy,
  };
}
